import { Lock } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { CampoSelect, CampoTexto } from "@/modules/visitantes/components/Campos";
import type { DadosSobre } from "@/modules/visitantes/services/visitantesService";

export type ErrosSobre = Partial<Record<"cidade" | "bairro" | "endereco", string>>;

interface Props {
  valor: DadosSobre;
  onMudar: (parcial: Partial<DadosSobre>) => void;
  erros?: ErrosSobre;
  /** Prefixo dos `name`/`id` dos campos (a tela pode ter várias pessoas ao mesmo tempo). */
  prefixo?: string;
  /** Com aviso de privacidade e moldura própria (formulário da pessoa). */
  comMoldura?: boolean;
  /** Mostra o pedido de oração. No cadastro em família ele é pedido à parte, para todos. */
  comOracao?: boolean;
}

/**
 * Perguntas de "Sobre a pessoa" (outra igreja, cidade, como prefere ser procurado, endereço,
 * pedido de oração). Usadas no formulário da pessoa e, no cadastro em família, de cada familiar
 * com 12 anos ou mais quando as respostas dele são diferentes (VIS-DEC-037).
 */
export function SobreAPessoa({
  valor,
  onMudar,
  erros = {},
  prefixo = "",
  comMoldura = true,
  comOracao = true,
}: Props) {
  const querVisita = valor.preferenciaContato === "visita";
  const campos = (
    <>
      <CampoSelect
        label="Frequenta outra igreja?"
        name={`${prefixo}igrejaSituacao`}
        value={valor.igrejaSituacao}
        onChange={(e) =>
          onMudar({ igrejaSituacao: e.target.value as DadosSobre["igrejaSituacao"] })
        }
      >
        <option value="">Não perguntei</option>
        <option value="outra">Sim, frequenta outra igreja</option>
        <option value="sem">Não frequenta nenhuma</option>
      </CampoSelect>

      {valor.igrejaSituacao === "outra" && (
        <Input
          label="Qual igreja? (opcional)"
          name={`${prefixo}igrejaNome`}
          maxLength={100}
          value={valor.igrejaNome}
          onChange={(e) => onMudar({ igrejaNome: e.target.value })}
        />
      )}

      <Input
        label={querVisita ? "Cidade *" : "Cidade onde mora"}
        name={`${prefixo}cidade`}
        maxLength={100}
        value={valor.cidade}
        onChange={(e) => onMudar({ cidade: e.target.value })}
        erro={erros.cidade}
      />

      <CampoSelect
        label="Como prefere ser procurado(a)?"
        name={`${prefixo}preferenciaContato`}
        value={valor.preferenciaContato}
        onChange={(e) =>
          onMudar({ preferenciaContato: e.target.value as DadosSobre["preferenciaContato"] })
        }
      >
        <option value="">Não perguntei</option>
        <option value="contato">Mensagem ou ligação</option>
        <option value="visita">Visita em casa</option>
        <option value="nao">Prefere não ser procurado(a)</option>
      </CampoSelect>

      {querVisita && (
        <>
          <Input
            label="Rua e número *"
            name={`${prefixo}endereco`}
            maxLength={200}
            value={valor.endereco}
            onChange={(e) => onMudar({ endereco: e.target.value })}
            erro={erros.endereco}
          />
          <Input
            label="Bairro *"
            name={`${prefixo}bairro`}
            maxLength={100}
            value={valor.bairro}
            onChange={(e) => onMudar({ bairro: e.target.value })}
            erro={erros.bairro}
          />
        </>
      )}

      {comOracao && (
        <CampoTexto
          label="Pedido de oração (opcional)"
          name={`${prefixo}pedidoOracao`}
          maxLength={500}
          value={valor.pedidoOracao}
          onChange={(e) => onMudar({ pedidoOracao: e.target.value })}
        />
      )}
    </>
  );

  if (!comMoldura) return <div className="flex flex-col gap-3">{campos}</div>;

  return (
    <fieldset className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <legend className="px-1 text-sm font-semibold text-text">Sobre a pessoa</legend>
      <p className="flex items-start gap-1.5 text-xs text-muted">
        <Lock size={14} className="mt-px shrink-0" aria-hidden="true" />
        Uso só para o cuidado da pessoa na igreja. Pergunte com naturalidade e respeite se ela não
        quiser responder.
      </p>
      {campos}
    </fieldset>
  );
}
